/**
 * Route canonicalisation for the translator-route-coverage capability.
 *
 * The bridge runs inside an iframe whose `window.location.pathname` looks like:
 *   - `/ko/pricing`              (marketing page under [locale])
 *   - `/pricing`                 (default locale; localePrefix: 'as-needed')
 *   - `/projects/abc-123-uuid`   (app page with a dynamic id)
 *
 * `normalizeRoute` strips the locale prefix (if it matches a known locale) and
 * collapses UUID / numeric-id segments to `:id` so routes that only differ in
 * a dynamic segment collapse to one entry.
 *
 * `isDynamicRoute` flags routes that contain a `:`-prefixed sentinel so the
 * picker can filter them and the parent shell can refuse to post them. V1 of
 * route coverage explicitly skips dynamic routes: a picker entry for `/projects/:id`
 * can't navigate anywhere without a sample concrete URL, and the seeded
 * checklist only needs the static user-facing routes, so dynamic routes are
 * kept out of `i18n_route_keys` (the backend rejects them with 400).
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const NUMERIC_RE = /^\d+$/

export function normalizeRoute(pathname: string, knownLocales: readonly string[]): string {
  const parts = pathname.split('/').filter(Boolean)
  const stripped = parts.length > 0 && knownLocales.includes(parts[0]) ? parts.slice(1) : parts
  const normalized = stripped.map(seg => {
    if (UUID_RE.test(seg)) return ':id'
    if (NUMERIC_RE.test(seg)) return ':id'
    return seg
  })

  if (normalized[0] === 'p' && normalized[1]) {
    normalized[1] = ':id'
  }
  if (normalized[0] === 'billing' && normalized[1] === 'project' && normalized[2]) {
    normalized[2] = ':id'
  }
  if (normalized[0] === 'settings' && normalized[1] === 'billing' && normalized[2] === 'project' && normalized[3]) {
    normalized[3] = ':id'
  }
  if (normalized[0] === 'team' && normalized[1] && normalized[1] !== 'activate' && normalized[1] !== 'cancel') {
    normalized[1] = ':id'
  }

  return '/' + normalized.join('/')
}

export function isDynamicRoute(route: string): boolean {
  return route.includes(':')
}

export function stripTranslateShellPrefix(pathname: string, knownLocales: readonly string[]): string {
  const parts = pathname.split('/').filter(Boolean)
  if (parts[0] === 'translate') parts.shift()
  if (parts[0] && knownLocales.includes(parts[0])) parts.shift()
  return '/' + parts.join('/')
}
